import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { 
  FiFileText, FiZap, FiTrendingUp, FiFilter, FiUserCheck, FiMessageSquare,
  FiSend, FiCheckCircle, FiCheck, FiCpu, FiPhoneCall, FiLayers
} = FiIcons;

const ScottyTab = () => {
  const { t } = useLanguage();
  const [activeChannel, setActiveChannel] = useState('Web Page');
  const [messages, setMessages] = useState([
    { from: 'bot', text: 'Hello! I am Scotty, your 24/7 event concierge. How can I help you navigate the summit today?' }
  ]);
  const [inputValue, setInputValue] = useState('');

  const features = [
    {
      icon: FiFileText,
      title: t('solutionsPage.scotty.features.f1Title') || "Knowledge-First Training",
      description: t('solutionsPage.scotty.features.f1Desc') || "Create chatbots that leverage your existing documents, PDFs, and technical content with no-code. Simply upload your event guide and Scotty learns instantly."
    },
    {
      icon: FiZap,
      title: t('solutionsPage.scotty.features.f2Title') || "Instant Deployment",
      description: t('solutionsPage.scotty.features.f2Desc') || "Kickstart by inserting an example system prompt. No complex training loops or data science degrees required. Go from zero to live in minutes."
    },
    {
      icon: FiTrendingUp,
      title: t('solutionsPage.scotty.features.f3Title') || "Sales Acceleration",
      description: t('solutionsPage.scotty.features.f3Desc') || "Custom AI chatbots to help you boost sales and cut costs with increased conversions. Let Scotty handle the FAQs while you close the VIP deals."
    },
    {
      icon: FiFilter,
      title: t('solutionsPage.scotty.features.f4Title') || "Noise Reduction",
      description: t('solutionsPage.scotty.features.f4Desc') || "Filter out the noise and repetitive questions. Scotty identifies high-intent leads so your support team can focus on your true fans only."
    },
    {
      icon: FiUserCheck,
      title: t('solutionsPage.scotty.features.f5Title') || "Enriched Segmentation",
      description: t('solutionsPage.scotty.features.f5Desc') || "Capture and export leads with enriched data points for better segmentation. Understand attendee needs before the event even starts."
    },
    {
      icon: FiMessageSquare,
      title: t('solutionsPage.scotty.features.f6Title') || "Omnichannel Reach",
      description: t('solutionsPage.scotty.features.f6Desc') || "Integrate with Messenger, WhatsApp and directly to you Event Page for a wider audience. Be wherever your attendees are."
    }
  ];

  const samplePrompts = [
    "What time is the keynote?",
    "Where is Hall B located?",
    "Can I upgrade to VIP Pass?"
  ];

  const handleSend = (text) => {
    const query = text || inputValue;
    if (!query.trim()) return;

    const newMsgs = [...messages, { from: 'user', text: query }];
    setMessages(newMsgs);
    setInputValue('');

    setTimeout(() => {
      let reply = "I've logged your request! You can find full details in your registration portal or check with on-site staff.";
      if (query.toLowerCase().includes('keynote')) {
        reply = "The Global Keynote starts at 9:00 AM on the Main Stage. Doors open at 8:30 AM with VIP early seating!";
      } else if (query.toLowerCase().includes('hall b') || query.toLowerCase().includes('where')) {
        reply = "Hall B is on Level 2 right next to the Innovation Lounge. Follow the digital overhead signage.";
      } else if (query.toLowerCase().includes('vip') || query.toLowerCase().includes('upgrade')) {
        reply = "Yes! VIP upgrades are available with expedited badge pickup, lounge access, and catered lunch. Would you like a direct checkout link?";
      }

      setMessages(prev => [...prev, { from: 'bot', text: reply }]);
    }, 500);
  };

  return (
    <div className="space-y-12 py-2">
      {/* Hero Header */}
      <div className="text-center max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-violet-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
          <SafeIcon icon={FiMessageSquare} className="w-3.5 h-3.5 text-violet-600 animate-pulse" />
          <span>{t('solutionsPage.scotty.badge') || "24/7 AI Guest Concierge"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          {t('solutionsPage.scotty.title') || "Meet Scotty. Your 24/7 AI Guest Concierge."}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {t('solutionsPage.scotty.subtitle') || "AI-powered guest concierge. Handles FAQs and lead capture 24/7 with zero human oversight."}
        </p>
      </div>

      {/* Interactive AI Concierge Playground */}
      <div className="glass-panel rounded-[28px] p-6 md:p-8 border border-white/80 shadow-xl shadow-slate-200/40 inner-glow relative overflow-hidden space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b border-slate-200/70">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-violet-500 animate-pulse" />
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">{t('solutionsPage.scotty.simulatorTitle') || "Scotty Omnichannel Chatbot Simulator"}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('solutionsPage.scotty.simulatorSubtitle') || "Trained directly on event documents & FAQs with instant lead enrichment."}</p>
          </div>

          {/* Channels Selector */}
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100/90 border border-slate-200/80 text-xs font-semibold">
            {['Web Page', 'WhatsApp', 'Messenger'].map((chan) => (
              <button
                key={chan}
                onClick={() => setActiveChannel(chan)}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  activeChannel === chan
                    ? 'bg-white text-violet-600 shadow-sm font-bold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {chan}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 items-start">
          {/* Chat Simulator (7 Cols) */}
          <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200/80 shadow-md p-4 flex flex-col h-[380px]">
            {/* Chat header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center font-bold text-xs">
                  🤖
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-900">Scotty AI ({activeChannel})</h4>
                  <span className="text-[10px] text-emerald-600 font-semibold">{t('solutionsPage.scotty.onlineStatus') || "Online • Sub-second response"}</span>
                </div>
              </div>
              <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100">
                {t('solutionsPage.scotty.pdfTrained') || "Trained on Event Guide PDF"}
              </span>
            </div>

            {/* Message Area */}
            <div className="flex-1 overflow-y-auto py-3 space-y-3 pr-1">
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-xs leading-relaxed ${
                      m.from === 'user'
                        ? 'bg-violet-600 text-white font-medium rounded-br-xs'
                        : 'bg-slate-100 text-slate-800 rounded-bl-xs'
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Prompt suggestions */}
            <div className="flex flex-wrap gap-1.5 py-2 border-t border-slate-100">
              {samplePrompts.map((p, i) => (
                <button
                  key={i}
                  onClick={() => handleSend(p)}
                  className="text-[10px] px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 hover:text-violet-700 hover:border-violet-200 hover:bg-violet-50 transition-all"
                >
                  {p}
                </button>
              ))}
            </div>

            {/* Input Bar */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex items-center gap-2 pt-2 border-t border-slate-100"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={t('solutionsPage.scotty.inputPlaceholder') || "Ask Scotty a question..."}
                className="flex-1 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs focus:outline-none focus:border-violet-500 focus:bg-white transition-all"
              />
              <button
                type="submit"
                className="p-2 rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-colors shadow-sm"
              >
                <SafeIcon icon={FiSend} className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>

          {/* Enriched Lead Analytics & Noise Reduction (5 Cols) */}
          <div className="lg:col-span-5 space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
              {t('solutionsPage.scotty.leadCaptureTitle') || "Intelligent Lead Capture & Triage"}
            </span>

            <div className="p-4 rounded-2xl bg-white/70 border border-slate-200/80 space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-slate-800">{t('solutionsPage.scotty.autoFiltered') || "Auto-Filtered Inquiries"}</span>
                <span className="font-bold text-emerald-600">{t('solutionsPage.scotty.resolvedRate') || "89.4% Resolved"}</span>
              </div>
              <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="w-[89%] h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full" />
              </div>
              <p className="text-[11px] text-slate-500">
                {t('solutionsPage.scotty.filterDesc') || "Repetitive FAQs handled without human staff intervention, freeing your team for VIP relations."}
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-violet-50/70 border border-violet-100 space-y-2">
              <div className="flex items-center gap-2 text-xs font-bold text-violet-900">
                <SafeIcon icon={FiUserCheck} className="w-4 h-4 text-violet-600" />
                <span>{t('solutionsPage.scotty.enrichedScoring') || "Enriched Lead Scoring"}</span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                {t('solutionsPage.scotty.scoringDesc') || "Scotty analyzes intent in real-time, tagging high-value prospects for immediate sales team follow-up."}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="p-3 rounded-xl bg-white/70 border border-slate-200/80 text-center">
                <div className="text-lg font-extrabold text-slate-900">2 min</div>
                <div className="text-[10px] text-slate-500 font-medium">{t('solutionsPage.scotty.zeroCode') || "Zero-Code Setup"}</div>
              </div>
              <div className="p-3 rounded-xl bg-white/70 border border-slate-200/80 text-center">
                <div className="text-lg font-extrabold text-violet-600">100%</div>
                <div className="text-[10px] text-slate-500 font-medium">{t('solutionsPage.scotty.accuracy') || "Document Accuracy"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* The 6 Core Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feat, idx) => (
          <motion.div
            key={idx}
            whileHover={{ y: -3 }}
            className="glass-card rounded-2xl p-6 border border-white/80 shadow-md shadow-slate-100/60 hover:shadow-xl hover:border-violet-200/80 transition-all flex flex-col justify-between"
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/10 to-purple-600/20 border border-violet-200/60 flex items-center justify-center text-violet-600 shadow-sm">
                <SafeIcon icon={feat.icon} className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900 tracking-tight">{feat.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-normal">{feat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ScottyTab;
